import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Download,
  FileCheck,
  XCircle,
  RotateCw,
  AlertTriangle,
  Copy,
  ClipboardList,
  Shield,
  Clock,
  CircleSlash,
  Layers,
  X,
} from 'lucide-react';
import * as api from '../lib/esignApi';
import { useAuth } from '../hooks/useAuth';
import { pdfjsLib } from '../lib/pdfWorker';
import SignInRequired from '../components/SignInRequired';

const RECIPIENT_STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-yellow-100 text-yellow-700',
  signed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
};

const ENVELOPE_STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  voided: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-700',
  declined: 'bg-red-100 text-red-700',
};

const EVENT_LABELS = {
  created: 'Envelope created',
  sent: 'Sent',
  viewed: 'Viewed by signer',
  signed: 'Signed',
  declined: 'Declined',
  completed: 'Completed',
  voided: 'Voided',
  expired: 'Expired',
  reminder_sent: 'Reminder sent',
  expiration_warning_sent: 'Expiration warning sent',
  downloaded: 'Downloaded',
  completion_failed: 'Finalization failed',
  duplicated: 'Duplicated',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCountdown(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const hrs = Math.floor(ms / (60 * 60 * 1000));
  if (hrs >= 48) return `${Math.floor(hrs / 24)} days left`;
  if (hrs >= 2) return `${hrs} hours left`;
  const mins = Math.max(1, Math.floor(ms / (60 * 1000)));
  return `${mins} minutes left`;
}

function PdfThumbnail({ envelopeId, onExpand }) {
  const canvasRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!envelopeId) return;
    let cancelled = false;
    (async () => {
      try {
        const buf = await api.getEnvelopeDocument(envelopeId);
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        const page = await doc.getPage(1);
        const vp = page.getViewport({ scale: 1 });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        const targetWidth = 320;
        const scale = targetWidth / vp.width;
        const scaledVp = page.getViewport({ scale });
        canvas.width = scaledVp.width;
        canvas.height = scaledVp.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaledVp }).promise;
        if (!cancelled) setLoaded(true);
      } catch (err) {
        console.error('thumbnail render failed', err);
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [envelopeId]);

  return (
    <button
      type="button"
      onClick={onExpand}
      className="relative block rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm hover:shadow-md transition"
      title="Open full document"
    >
      {!loaded && !failed && (
        <div className="flex items-center justify-center w-[320px] h-[420px]">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      )}
      {failed && (
        <div className="flex items-center justify-center w-[320px] h-[420px] text-sm text-gray-500">
          Preview unavailable
        </div>
      )}
      <canvas ref={canvasRef} className={loaded ? 'block' : 'hidden'} />
    </button>
  );
}

export default function EsignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [resending, setResending] = useState({}); // recipientId -> 'loading'|'done'|'throttled'
  const [copied, setCopied] = useState({}); // recipientId -> true
  const [actionConfirm, setActionConfirm] = useState(null); // 'void' | null
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const fresh = await api.getEnvelope(id);
      setData(fresh);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (authLoading || !user) return;
    setLoading(true);
    load();
    pollRef.current = setInterval(load, 30000);
    return () => clearInterval(pollRef.current);
  }, [load, authLoading, user]);

  useEffect(() => {
    if (!data?.envelope) return;
    if (data.envelope.status !== 'sent' || !data.envelope.expires_at) {
      setCountdown(null);
      return;
    }
    const tick = () => setCountdown(formatCountdown(data.envelope.expires_at));
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [data?.envelope]);

  useEffect(() => {
    if (!id || authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { url } = await api.getOriginalUrl(id);
        if (!cancelled) setPdfUrl(url);
      } catch (err) {
        console.error('failed to fetch original PDF url', err);
      }
    })();
    return () => { cancelled = true; };
  }, [id, authLoading, user]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleResend = useCallback(
    async (recipient) => {
      setResending((s) => ({ ...s, [recipient.id]: 'loading' }));
      try {
        await api.resendInvite(id, recipient.id);
        setResending((s) => ({ ...s, [recipient.id]: 'done' }));
        setTimeout(() => {
          setResending((s) => {
            const next = { ...s };
            delete next[recipient.id];
            return next;
          });
        }, 3000);
        load();
      } catch (err) {
        if (err.status === 429) {
          setResending((s) => ({ ...s, [recipient.id]: 'throttled' }));
          setTimeout(() => {
            setResending((s) => {
              const next = { ...s };
              delete next[recipient.id];
              return next;
            });
          }, 5000);
        } else {
          setResending((s) => {
            const next = { ...s };
            delete next[recipient.id];
            return next;
          });
          setToast({ kind: 'error', text: err.message });
        }
      }
    },
    [id, load]
  );

  const handleCopyLink = useCallback((recipient) => {
    const signUrl = `${window.location.origin}/sign/${recipient.sign_token}`;
    navigator.clipboard?.writeText(signUrl).then(() => {
      setCopied((c) => ({ ...c, [recipient.id]: true }));
      setTimeout(() => {
        setCopied((c) => {
          const next = { ...c };
          delete next[recipient.id];
          return next;
        });
      }, 2000);
    });
  }, []);

  const handleVoid = useCallback(async () => {
    setActionBusy(true);
    try {
      await api.voidEnvelope(id);
      setActionConfirm(null);
      setToast({ kind: 'success', text: 'Envelope voided.' });
      load();
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    } finally {
      setActionBusy(false);
    }
  }, [id, load]);

  const handleDuplicate = useCallback(async () => {
    setActionBusy(true);
    try {
      const result = await api.duplicateEnvelope(id);
      setToast({ kind: 'success', text: 'Duplicate created. Opening…' });
      setTimeout(() => navigate(`/esign/${result.id}`), 400);
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
      setActionBusy(false);
    }
  }, [id, navigate]);

  const handleDownload = useCallback(
    async (kind) => {
      try {
        let url;
        if (kind === 'original') {
          url = (await api.getOriginalUrl(id)).url;
        } else if (kind === 'signed') {
          url = (await api.getCompletedUrl(id)).url;
        } else if (kind === 'certificate') {
          url = (await api.getCertificateUrl(id)).url;
        }
        if (url) window.open(url, '_blank');
      } catch (err) {
        setToast({ kind: 'error', text: err.message });
      }
    },
    [id]
  );

  const handleDownloadAudit = useCallback(async () => {
    try {
      const csv = await api.downloadAuditCsv(id);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `envelope-${id}-audit.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    }
  }, [id]);

  // Logged-out (e.g. a licensed desktop user who signed out): eSign needs a
  // live session — prompt sign-in instead of a dead page.
  if (!authLoading && !user) {
    return <SignInRequired feature="eSign" />;
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (!data?.envelope) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Envelope not found.</p>
        <Link to="/esign" className="text-indigo-600 hover:underline text-sm mt-2 inline-block">
          Back to envelopes
        </Link>
      </div>
    );
  }

  const envelope = data.envelope;
  const recipients = data.recipients || [];
  const events = data.events || [];
  const isCompleted = envelope.status === 'completed';
  const isVoided = envelope.status === 'voided';
  const isSent = envelope.status === 'sent';
  const isExpired = envelope.status === 'expired';

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <Link
        to="/esign"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition mb-5"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to envelopes
      </Link>

      {toast && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm border ${
            toast.kind === 'error'
              ? 'bg-red-50 border-red-200 text-red-700'
              : toast.kind === 'warn'
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          {toast.text}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Status banner */}
      {isCompleted && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">All signatures collected</p>
            <p className="text-xs text-green-600 mt-0.5">
              Completed {formatDate(envelope.completed_at)}
            </p>
          </div>
        </div>
      )}
      {isVoided && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <CircleSlash className="w-5 h-5 text-red-500 shrink-0" />
          <span className="text-sm font-medium text-red-800">This envelope has been voided.</span>
        </div>
      )}
      {isExpired && (
        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">This envelope has expired.</p>
            <p className="text-xs text-amber-700 mt-0.5">
              You can duplicate it to start over with the same recipients and fields.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-6">
        {/* Thumbnail */}
        <div>
          {id ? (
            <PdfThumbnail envelopeId={id} onExpand={() => setExpanded(true)} />
          ) : (
            <div className="w-[320px] h-[420px] rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2 text-center">
            {envelope.original_page_count || 1} page{(envelope.original_page_count || 1) !== 1 ? 's' : ''}
          </p>
        </div>

        <div>
          {/* Envelope header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 mb-1 truncate">
                  {envelope.subject || 'Untitled'}
                </h1>
                <p className="text-xs text-gray-500 truncate">
                  {envelope.original_filename || 'document.pdf'}
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-2 flex-wrap">
                  <span>Created {formatDate(envelope.created_at)}</span>
                  {envelope.sent_at && <span>Sent {formatDate(envelope.sent_at)}</span>}
                  {isSent && countdown && (
                    <span className="text-amber-700 font-medium">{countdown}</span>
                  )}
                </div>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                  ENVELOPE_STATUS_STYLES[envelope.status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {envelope.status}
              </span>
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Recipients
            </h2>
            <div className="space-y-3">
              {recipients.map((r) => (
                <div
                  key={r.id || r.email}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">{r.email}</p>
                    {r.signed_at && (
                      <p className="text-xs text-green-600 mt-0.5">Signed {formatDate(r.signed_at)}</p>
                    )}
                    {r.declined_at && (
                      <p className="text-xs text-red-600 mt-0.5">
                        Declined {formatDate(r.declined_at)}
                        {r.decline_reason && ` — ${r.decline_reason}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        RECIPIENT_STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {r.status}
                    </span>

                    {isSent && ['pending', 'sent', 'viewed'].includes(r.status) && (
                      <>
                        <button
                          onClick={() => handleResend(r)}
                          disabled={resending[r.id] === 'loading'}
                          className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50 flex items-center gap-1"
                        >
                          {resending[r.id] === 'loading' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : resending[r.id] === 'done' ? (
                            <span className="text-green-600">Sent!</span>
                          ) : resending[r.id] === 'throttled' ? (
                            <span className="text-amber-600">Throttled</span>
                          ) : (
                            <>
                              <RotateCw className="w-3 h-3" />
                              Resend
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleCopyLink(r)}
                          className="text-xs text-gray-600 hover:text-gray-800 font-medium flex items-center gap-1"
                        >
                          {copied[r.id] ? (
                            <span className="text-green-600">Copied!</span>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              Copy link
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Actions
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleDownload('original')}
                className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                Original
              </button>

              {isCompleted && (
                <>
                  <button
                    onClick={() => handleDownload('signed')}
                    className="bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 text-sm"
                  >
                    <FileCheck className="w-4 h-4" />
                    Signed PDF
                  </button>
                  <button
                    onClick={() => handleDownload('certificate')}
                    className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 text-sm"
                  >
                    <Shield className="w-4 h-4" />
                    Certificate
                  </button>
                </>
              )}

              <button
                onClick={handleDownloadAudit}
                className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 text-sm"
              >
                <ClipboardList className="w-4 h-4" />
                Audit CSV
              </button>

              {(isCompleted || isExpired || isVoided) && (
                <button
                  onClick={handleDuplicate}
                  disabled={actionBusy}
                  className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
              )}

              <button
                onClick={() => setSaveTemplateOpen(true)}
                className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 text-sm"
              >
                <Layers className="w-4 h-4" />
                Save as template
              </button>

              {isSent && (
                <button
                  onClick={() => setActionConfirm('void')}
                  className="ml-auto border border-red-200 text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition flex items-center gap-2 text-sm"
                >
                  <XCircle className="w-4 h-4" />
                  Void
                </button>
              )}
            </div>

            {isCompleted && (
              <div className="mt-4 text-xs text-gray-500 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-gray-400" />
                Anyone can verify this document at{' '}
                <a
                  href={`${window.location.origin}/verify/${envelope.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  /verify/{envelope.id.slice(0, 8)}…
                </a>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Activity
            </h2>
            <ol className="space-y-3">
              {events.map((event) => (
                <li key={event.id} className="flex items-start gap-3 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 flex items-center justify-between gap-2">
                      <span className="font-medium">{EVENT_LABELS[event.kind] || event.kind}</span>
                      <span className="text-xs text-gray-400 shrink-0">{formatDate(event.created_at)}</span>
                    </div>
                    {event.actor_email && (
                      <div className="text-xs text-gray-500">{event.actor_email}</div>
                    )}
                  </div>
                </li>
              ))}
              {events.length === 0 && (
                <li className="text-sm text-gray-400 italic">No activity yet.</li>
              )}
            </ol>
          </div>
        </div>
      </div>

      {/* Void confirm */}
      {actionConfirm === 'void' && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Void envelope?</h3>
                <p className="text-sm text-gray-600">
                  This cancels all pending signatures. Recipients will be notified. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setActionConfirm(null)}
                disabled={actionBusy}
                className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={actionBusy}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {actionBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Yes, void
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded preview */}
      {expanded && pdfUrl && (
        <div
          className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setExpanded(false)}
        >
          <div className="bg-white rounded-xl max-w-5xl w-full h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <iframe src={pdfUrl} title="Document preview" className="w-full h-full" />
          </div>
        </div>
      )}

      {saveTemplateOpen && (
        <SaveTemplateModal
          envelope={envelope}
          onClose={() => setSaveTemplateOpen(false)}
          onSaved={() => {
            setSaveTemplateOpen(false);
            setToast({ kind: 'success', text: 'Template saved. Visit /esign/templates to reuse it.' });
          }}
          onError={(msg) => setToast({ kind: 'error', text: msg })}
        />
      )}
    </div>
  );
}

function SaveTemplateModal({ envelope, onClose, onSaved, onError }) {
  const [name, setName] = useState(envelope?.subject || '');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.createTemplateFromEnvelope({
        sourceEnvelopeId: envelope.id,
        name: trimmed,
        description: description.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      if (err.body?.code === 'TEMPLATE_CAP_REACHED') {
        onError(err.message);
      } else {
        onError(err.message);
      }
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Save as template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Saves the document, recipient roles (placeholders — emails are stripped), and field
          layout for reuse.
        </p>

        <label className="block mb-3">
          <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={128}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g. Onboarding Packet"
          />
        </label>

        <label className="block mb-4">
          <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
            Description (optional)
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Internal notes about when to use this template"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save template
          </button>
        </div>
      </div>
    </div>
  );
}
